/**
 * پارت — وضعیت (ورود/سفارش/مانده) و سفارش‌ها **بالا**، پارت‌های لینک‌شده (ادغام/انتقال)، و مشخصات برای ویرایش **پایین**.
 * کاربر (v2.169.0): «در پارت اولویت با سفارش‌هاست؛ تاریخِ ورود، نخ، دهانه و … برای ویرایش پایین‌تر بیاید».
 * همه‌چیزِ پارت هر لحظه قابلِ ویرایش است؛ پارت «بسته» نمی‌شود. انتقال/ادغام فقط بینِ پارت‌های **هم‌نخ**.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button, Grid, Input, Modal, Popconfirm, Select, Space, Tag, message } from "antd";
import {
  BranchesOutlined, CheckOutlined, DeleteOutlined, LinkOutlined, PlusOutlined, SaveOutlined, SwapLeftOutlined,
} from "@ant-design/icons";
import api from "../../api/client";
import { apiError } from "../../utils/apiError";
import { JalaliDateInput, todayISO } from "../../utils/dateUtils";
import { useDraft } from "../../utils/draft";
import DraftBar from "../../components/DraftBar";
import { BASE, DELIVERY, IntInput, QuickPick, STATUS, fa, lbl, n0, toEn } from "./dyeShared";

const EMPTY = { entry_date: null, dyehouse: null, yarn: null, tagheh: null, opening: null, dye_code: null,
  width: null, tracking_code: null, notes: null, is_opening: false };
const FIELDS = Object.keys(EMPTY);
const isEmptyForm = (v) => !v || FIELDS.every((k) => ["entry_date", "dyehouse", "is_opening"].includes(k) || v[k] == null || v[k] === "");

export function LotLink({ lot, onOpen, extra }) {
  if (!lot) return null;
  return (
    <Tag className="dye-link" color="blue" onClick={() => onOpen(lot.id)}>
      <LinkOutlined /> {lbl(lot.label)}{extra ? ` · ${extra}` : ""}
    </Tag>
  );
}

/** خلاصهٔ گروهِ ادغام‌شده: هر پارت با ورود/سفارش/مانده + جمع (کاربر: «ادغام فقط کد را می‌نوشت؛ باید بگوید ۲۵۰ بوده، چقدر سفارش شده»). */
function MergeGroup({ lots, current, onOpen }) {
  const t = lots.reduce((s, l) => ({ in: s.in + n0(l.tagheh) + n0(l.transfer_in), ord: s.ord + n0(l.ordered),
    out: s.out + n0(l.transfer_out), rem: s.rem + n0(l.remaining) }), { in: 0, ord: 0, out: 0, rem: 0 });
  return (
    <div className="dye-group">
      <div className="dye-group-r dye-group-h"><span>پارت</span><span>ورود</span><span>سفارش</span><span>مانده</span></div>
      {lots.map((l) => (
        <div key={l.id} className={"dye-group-r" + (l.id === current ? " dye-cur" : " dye-click")}
          onClick={() => l.id !== current && onOpen(l.id)}>
          <span><b>{lbl(l.label)}</b> <span className="dye-mute">{fa(l.entry_date_fa)}</span></span>
          <span>{fa(n0(l.tagheh) + n0(l.transfer_in))}</span><span>{fa(l.ordered)}</span>
          <span className={l.remaining < 0 ? "dye-neg" : ""}>{fa(l.remaining)}</span>
        </div>
      ))}
      <div className="dye-group-r dye-group-t"><span>جمع</span><span>{fa(t.in)}</span><span>{fa(t.ord)}</span><span>{fa(t.rem)}</span></div>
      {t.out > 0 && <div className="dye-mute">{fa(t.out)} طاقه از این گروه به پارتِ دیگر منتقل شده.</div>}
    </div>
  );
}

const newColor = () => ({ color_name: null, color_code: null, tagheh: null });

/**
 * افزودنِ رنگ از داخلِ خودِ پارت (کاربر v2.170: «از داخلِ همان پارت هم بشود رنگ‌ها را اضافه کرد»).
 * پیش‌فرض: به آخرین سفارشِ تحویل‌نشدهٔ همین پارت اضافه می‌شود؛ یا «سفارشِ تازه» با تاریخ و رنگرزیِ همین پارت.
 */
function AddColors({ lot, orders, options, onSaved }) {
  const openOrders = orders.filter((o) => o.status !== "delivered");
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState("new");
  const [busy, setBusy] = useState(false);
  const start = () => { setRows([newColor()]); setTarget(openOrders[0]?.id ?? "new"); };
  const set = (i, patch) => setRows((r) => r.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const total = (rows || []).reduce((s, c) => s + n0(c.tagheh), 0);
  if (!rows) {
    return <Button block type="primary" icon={<PlusOutlined />} disabled={lot.remaining <= 0} onClick={start}
      style={{ marginBottom: 8 }}>افزودنِ رنگ به این پارت {lot.remaining > 0 ? `(مانده ${fa(lot.remaining)})` : ""}</Button>;
  }
  const save = async () => {
    const add = rows.filter((c) => c.color_name || c.tagheh);
    if (!add.length || add.some((c) => !c.color_name || !c.tagheh)) return message.warning("برای هر رنگ نام و تعدادِ طاقه بنویسید");
    if (total > lot.remaining) return message.warning(`ماندهٔ این پارت ${fa(lot.remaining)} طاقه است؛ ${fa(total)} طاقه شد`);
    setBusy(true);
    try {
      if (target === "new") {
        await api.post(`${BASE}/orders`, { lots: [{ lot_id: lot.id }], order_date: lot.entry_date, dyehouse: lot.dyehouse,
          colors: add });
      } else {
        const o = orders.find((x) => x.id === target);
        await api.put(`${BASE}/orders/${o.id}`, {
          order_date: o.order_date, code: o.code_manual || null, dyehouse: o.dyehouse, driver: o.driver,
          freight_amount: o.freight_amount, freight_paid: o.freight_paid, notes: o.notes,
          lots: o.lots.map((x) => ({ lot_id: x.lot_id })),
          colors: [...o.colors.map((c) => ({ color_name: c.color_name, color_code: c.color_code, tagheh: c.tagheh, width: c.width })), ...add],
        });
      }
      message.success("رنگ‌ها ثبت شد");
      setRows(null);
      await onSaved?.();
    } catch (e) { message.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="dye-box" style={{ marginBottom: 8 }}>
      {openOrders.length > 0 && (
        <Select size="large" value={target} onChange={setTarget} style={{ width: "100%" }} options={[
          ...openOrders.map((o) => ({ value: o.id, label: <span>اضافه به سفارش {lbl(o.code)} ({o.colors.map((c) => c.color_name).join("، ")})</span> })),
          { value: "new", label: "سفارشِ تازه" },
        ]} />
      )}
      {rows.map((c, i) => (
        <div className="dye-color-edit" key={i} style={{ background: "var(--surface)", marginBottom: 0 }}>
          <QuickPick value={c.color_name} placeholder="رنگ (سبز، یشمی، …)" options={options.color}
            onChange={(v) => set(i, { color_name: v })}
            onPickOption={(o) => o.code && set(i, { color_name: o.value, color_code: o.code })} />
          <div className="dye-color-edit-r">
            <Input size="large" placeholder="کد رنگ" value={fa(c.color_code)} onChange={(e) => set(i, { color_code: toEn(e.target.value) || null })} />
            <IntInput placeholder="طاقه" value={c.tagheh} onChange={(v) => set(i, { tagheh: v })} />
            <Button size="large" type="text" danger icon={<DeleteOutlined />} disabled={rows.length < 2}
              onClick={() => setRows((r) => r.filter((_, j) => j !== i))} />
          </div>
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => setRows((r) => [...r, newColor()])}>رنگِ دیگر</Button>
      <div className={"dye-total" + (total > lot.remaining ? " dye-neg" : "")} style={{ margin: 0 }}>
        <span>مانده <b>{fa(lot.remaining)}</b></span><span>این رنگ‌ها <b>{fa(total)}</b></span>
        <span>باقی <b>{fa(lot.remaining - total)}</b></span>
      </div>
      <Space>
        <Button type="primary" size="large" loading={busy} onClick={save}>ثبتِ رنگ‌ها</Button>
        <Button size="large" onClick={() => setRows(null)}>انصراف</Button>
      </Space>
    </div>
  );
}

export default function DyeLotSheet({ open, lotId, isNew, board, options, onClose, onSaved, onOpenLot, onOpenOrder,
  onNewOrder, canEdit, canDelete }) {
  const screens = Grid.useBreakpoint();
  const lot = useMemo(() => board.lots.find((l) => l.id === lotId) || null, [board, lotId]);
  const lotsById = useMemo(() => Object.fromEntries(board.lots.map((l) => [l.id, l])), [board]);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [xfer, setXfer] = useState(null);       // {to, tagheh}
  const [merge, setMerge] = useState(null);     // {ids, code}
  const draft = useDraft(open && isNew ? "dye:lot:new" : null, f, { isEmpty: isEmptyForm, enabled: open && !!isNew });

  useEffect(() => {
    if (!open) return;
    // کدِ رنگرزیِ قدیمی در «کدِ رهگیری» نشان داده می‌شود و با ذخیره همان‌جا می‌نشیند (v2.170: یکی بود)
    if (lot) setF({ ...Object.fromEntries(FIELDS.map((k) => [k, lot[k] ?? EMPTY[k]])), tracking_code: lot.track || null, dye_code: null });
    else {
      // پارتِ تازه: تاریخِ امروز و رنگرزیِ آخرین پارت (معمولاً همان است)
      const last = board.lots[0];
      setF({ ...EMPTY, entry_date: todayISO(), dyehouse: last?.dyehouse || null });
    }
    setXfer(null); setMerge(null);
    // `!!lot`: با بازشدنِ مستقیم از آدرس، فرم بعد از رسیدنِ داده پر شود (نه خالی به‌عنوانِ پارتِ تازه)
  }, [open, lotId, !!lot]);   // eslint-disable-line

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v === "" ? null : v }));

  const save = async (andNext) => {
    if (!f.entry_date) return message.warning("تاریخِ ورود به رنگرزی را بزنید");
    if (f.tagheh === null || f.tagheh === undefined) return message.warning("تعدادِ طاقه را بنویسید");
    setSaving(true);
    try {
      if (lot) {
        await api.patch(`${BASE}/lots/${lot.id}`, f);
        message.success("ذخیره شد");
        await onSaved?.(lot.id);
      } else {
        const r = await api.post(`${BASE}/lots`, f);
        draft.clear();
        if (andNext) {
          // پارتِ بعدی: همان تاریخ/رنگرزی/نخ/دهانه/عرض بماند — فقط طاقه و کدها از نو
          setF((s) => ({ ...s, tagheh: null, dye_code: null, tracking_code: null, notes: null }));
          message.success("ثبت شد — پارتِ بعدی");
          await onSaved?.(null);
        } else {
          await onSaved?.(r.data.id);
        }
      }
    } catch (e) {
      message.error(apiError(e));
    }
    setSaving(false);
  };

  const call = async (fn, ok) => {
    try { await fn(); if (ok) message.success(ok); await onSaved?.(lot?.id ?? null); return true; } catch (e) {
      message.error(apiError(e)); return false;
    }
  };
  const del = async () => { if (await call(() => api.delete(`${BASE}/lots/${lot.id}`), "پارت حذف شد")) onClose(); };
  const doTransfer = async () => {
    if (!xfer?.to || !xfer?.tagheh) return message.warning("پارتِ مقصد و تعداد را بزنید");
    if (await call(() => api.post(`${BASE}/transfers`, { from_lot_id: lot.id, to_lot_id: xfer.to, tagheh: xfer.tagheh,
      transfer_date: todayISO() }), "منتقل شد")) setXfer(null);
  };
  const doMerge = async () => {
    if (!merge?.ids?.length) return message.warning("پارت‌هایی که با این پارت ادغام شده‌اند را انتخاب کنید");
    if (await call(() => api.post(`${BASE}/lots/merge`, { lot_ids: [lot.id, ...merge.ids], dye_code: merge.code || null }),
      "ادغام شد")) setMerge(null);
  };

  const ins = lot ? board.transfers.filter((t) => t.to_lot_id === lot.id) : [];
  const outs = lot ? board.transfers.filter((t) => t.from_lot_id === lot.id) : [];
  const orders = lot ? board.orders.filter((o) => o.lots.some((x) => x.lot_id === lot.id)) : [];
  const group = lot && lot.merged_with.length ? [lot, ...lot.merged_with.map((id) => lotsById[id]).filter(Boolean)]
    .sort((a, b) => (a.entry_date < b.entry_date ? -1 : a.entry_date > b.entry_date ? 1 : a.id - b.id)) : [];
  // انتقال/ادغام فقط بینِ هم‌نخ‌ها (کاربر: «هر نخ با خودش رنگ می‌شود؛ فقط به مثلِ خودش منتقل شود»)
  const others = board.lots.filter((l) => lot && l.id !== lot.id && (!lot.yarn || !l.yarn || l.yarn === lot.yarn));
  const lotOpt = (l) => ({ value: l.id, short: lbl(l.label),
    label: `${lbl(l.label)} · ${fa(l.yarn || "—")} · ورود ${fa(n0(l.tagheh) + n0(l.transfer_in))} · سفارش ${fa(l.ordered)} · مانده ${fa(l.remaining)}` });

  const form = (
    <>
      <div className="dye-form">
        <div className="dye-f dye-half"><span>تاریخ ورود به رنگرزی *</span>
          <JalaliDateInput value={f.entry_date} onChange={set("entry_date")} style={{ width: "100%", minHeight: 40 }} /></div>
        <div className="dye-f dye-half"><span>تعداد طاقه *</span>
          <IntInput value={f.tagheh} onChange={set("tagheh")} placeholder="مثلاً ۹۰" /></div>
        <div className="dye-f"><span>نخ</span>
          <QuickPick value={f.yarn} onChange={set("yarn")} options={options.yarn} chips={6} placeholder="انتخاب یا تایپ" /></div>
        <div className="dye-f"><span>دهانهٔ بافت</span>
          <QuickPick value={f.opening} onChange={set("opening")} options={options.opening} chips={8} numeric placeholder="۳۶، ۳۸، …" /></div>
        <div className="dye-f dye-half"><span>عرض درخواستی</span>
          <QuickPick value={f.width} onChange={set("width")} options={options.width} numeric placeholder="۱۰۰، ۱۰۶" /></div>
        <div className="dye-f dye-half"><span>رنگرزی</span>
          <QuickPick value={f.dyehouse} onChange={set("dyehouse")} options={options.dyehouse} placeholder="نامِ رنگرزی" /></div>
        <div className="dye-f"><span>کد رهگیریِ رنگرزی</span>
          <Input size="large" inputMode="numeric" value={fa(f.tracking_code)} onChange={(e) => set("tracking_code")(toEn(e.target.value))}
            placeholder="هر وقت رنگرزی داد" /></div>
        <div className="dye-f"><span>توضیح</span>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} value={f.notes ?? ""} onChange={(e) => set("notes")(e.target.value)} /></div>
      </div>
      {canEdit && (
        <div className="dye-actions">
          <Button type="primary" size="large" icon={<SaveOutlined />} loading={saving} onClick={() => save(false)}>
            {lot ? "ذخیرهٔ مشخصات" : "ثبت پارت"}</Button>
          {!lot && <Button size="large" loading={saving} onClick={() => save(true)}>ثبت و پارتِ بعدی</Button>}
        </div>
      )}
    </>
  );

  const detail = lot && (
    <>
      <div className="dye-balance">
        <div><b>{fa(lot.tagheh)}</b><span>ورودِ خام</span></div>
        {lot.transfer_in > 0 && <div><b>+{fa(lot.transfer_in)}</b><span>انتقالی</span></div>}
        {lot.transfer_out > 0 && <div><b>−{fa(lot.transfer_out)}</b><span>منتقل‌شده</span></div>}
        <div><b>−{fa(lot.ordered)}</b><span>سفارش</span></div>
        <div className={lot.remaining < 0 ? "dye-neg" : lot.remaining > 0 ? "dye-pos" : ""}>
          <b>{fa(lot.remaining)}</b><span>مانده</span></div>
      </div>
      <div className="dye-row">
        {lot.is_opening && <Tag color="gold">اولِ دوره</Tag>}
        {lot.yarn && <Tag>نخ {fa(lot.yarn)}</Tag>}
        {lot.delivery !== "none" && <Tag color={DELIVERY[lot.delivery].color}>
          {DELIVERY[lot.delivery].label}{lot.delivery === "partial" ? ` ${fa(lot.delivered)} از ${fa(lot.ordered)}` : ""}</Tag>}
        <span className="dye-grow" />
        {canEdit && orders.length > 0 && lot.delivery !== "full" && (
          <Popconfirm title="همهٔ رنگ‌های همهٔ سفارش‌های این پارت «تحویل شد» بخورد؟"
            onConfirm={() => call(() => api.post(`${BASE}/lots/${lot.id}/deliver-all`, {}), "کلِ پارت تحویل شد")}>
            <Button size="small" type="primary" ghost icon={<CheckOutlined />}>تحویلِ کاملِ پارت</Button>
          </Popconfirm>
        )}
      </div>

      <div className="dye-sec">رنگ‌ها و سفارش‌های این پارت</div>
      {canEdit && <AddColors lot={lot} orders={orders} options={options} onSaved={() => onSaved?.(lot.id)} />}
      {orders.length === 0 && <div className="dye-mute">هنوز رنگی برای این پارت ثبت نشده.</div>}
      {orders.map((o) => {
        const mine = o.lots.find((x) => x.lot_id === lot.id);
        return (
          <div className="dye-ocard" key={o.id} onClick={() => onOpenOrder(o.id)}>
            <div className="dye-card-h">
              <b>سفارش {lbl(o.code)}</b><span className="dye-mute">{fa(o.order_date_fa)}</span>
              <span className="dye-grow" /><Tag color={STATUS[o.status].color}>{STATUS[o.status].label}</Tag>
            </div>
            <div className="dye-card-f">
              {o.colors.map((c) => <span key={c.id}>{c.color_name} {fa(c.tagheh)}</span>)}
              <span className="dye-mute">· از این پارت {fa(mine?.tagheh)}{o.lots.length > 1 ? ` (از ${fa(o.total)})` : ""}</span>
            </div>
          </div>
        );
      })}
      {canEdit && (
        <Button block type="primary" ghost icon={<PlusOutlined />} disabled={lot.remaining <= 0} style={{ marginTop: 6 }}
          onClick={() => onNewOrder([lot.id, ...lot.merged_with.filter((id) => n0(lotsById[id]?.remaining) > 0
            && (!lot.yarn || lotsById[id]?.yarn === lot.yarn))])}>
          سفارشِ کامل (چند پارت، حمل، …)</Button>
      )}

      <div className="dye-sec">پارت‌های لینک‌شده</div>
      {group.length > 0 && (
        <>
          <div className="dye-mute" style={{ marginBottom: 4 }}><BranchesOutlined /> ادغام — کدِ رهگیریِ مشترک {fa(lot.track)}</div>
          <MergeGroup lots={group} current={lot.id} onOpen={onOpenLot} />
        </>
      )}
      {ins.map((t) => (
        <div className="dye-row" key={`i${t.id}`}><span className="dye-k">از پارتِ</span>
          <span><LotLink lot={lotsById[t.from_lot_id]} onOpen={onOpenLot} /> {fa(t.tagheh)} طاقه آمد · {fa(t.transfer_date_fa)}</span>
          {canEdit && <Popconfirm title="این انتقال حذف شود؟" onConfirm={() => call(() => api.delete(`${BASE}/transfers/${t.id}`), "انتقال حذف شد")}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}</div>
      ))}
      {outs.map((t) => (
        <div className="dye-row" key={`o${t.id}`}><span className="dye-k">به پارتِ</span>
          <span><LotLink lot={lotsById[t.to_lot_id]} onOpen={onOpenLot} /> {fa(t.tagheh)} طاقه رفت · {fa(t.transfer_date_fa)}</span>
          {canEdit && <Popconfirm title="این انتقال حذف شود؟" onConfirm={() => call(() => api.delete(`${BASE}/transfers/${t.id}`), "انتقال حذف شد")}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}</div>
      ))}
      {!group.length && !ins.length && !outs.length && <div className="dye-mute">هنوز به پارتِ دیگری وصل نیست.</div>}

      {canEdit && (
        <div className="dye-actions">
          <Button icon={<SwapLeftOutlined />} disabled={lot.remaining <= 0}
            onClick={() => setXfer({ to: null, tagheh: lot.remaining })}>انتقالِ مانده</Button>
          <Button icon={<BranchesOutlined />} onClick={() => setMerge({ ids: lot.merged_with, code: lot.track })}>ادغام با پارت</Button>
        </div>
      )}
      {xfer && (
        <div className="dye-box">
          <div className="dye-mute">ماندهٔ این پارت ({fa(lot.remaining)} طاقه) به کدام پارت رفت؟ فقط پارت‌های هم‌نخ.</div>
          <Select size="large" showSearch optionFilterProp="label" optionLabelProp="short" placeholder="پارتِ مقصد"
            style={{ width: "100%" }} value={xfer.to} onChange={(v) => setXfer((s) => ({ ...s, to: v }))} options={others.map(lotOpt)} />
          <Space.Compact style={{ width: "100%" }}>
            <IntInput value={xfer.tagheh} onChange={(v) => setXfer((s) => ({ ...s, tagheh: v }))} placeholder="تعداد طاقه" />
            <Button size="large" type="primary" onClick={doTransfer}>ثبت انتقال</Button>
          </Space.Compact>
          <Button type="link" onClick={() => setXfer(null)}>انصراف</Button>
        </div>
      )}
      {merge && (
        <div className="dye-box">
          <div className="dye-mute">پارت‌هایی که با این پارت یکی شده‌اند (تاریخ و طاقه جدا می‌ماند، کدِ رهگیری مشترک می‌شود). فقط هم‌نخ‌ها:</div>
          <Select size="large" mode="multiple" showSearch optionFilterProp="label" optionLabelProp="short" style={{ width: "100%" }}
            value={merge.ids} onChange={(v) => setMerge((s) => ({ ...s, ids: v }))} options={others.map(lotOpt)} />
          {merge.ids.length > 0 && (
            <MergeGroup lots={[lot, ...merge.ids.map((id) => lotsById[id]).filter(Boolean)]} current={lot.id} onOpen={() => {}} />
          )}
          <Space.Compact style={{ width: "100%" }}>
            <Input size="large" inputMode="numeric" placeholder="کدِ رهگیریِ مشترک" value={fa(merge.code)}
              onChange={(e) => setMerge((s) => ({ ...s, code: toEn(e.target.value) }))} />
            <Button size="large" type="primary" onClick={doMerge}>ادغام</Button>
          </Space.Compact>
          <Button type="link" onClick={() => setMerge(null)}>انصراف</Button>
        </div>
      )}

      <div className="dye-sec">مشخصاتِ پارت</div>
      {form}
      <div className="dye-stamp">
        ثبت: {fa(lot.created_at_fa)}{lot.created_by ? ` (${lot.created_by})` : ""}
        {lot.updated_at_fa && lot.updated_at_fa !== lot.created_at_fa
          ? ` — آخرین ویرایش: ${fa(lot.updated_at_fa)}${lot.updated_by ? ` (${lot.updated_by})` : ""}` : ""}
      </div>
      {canDelete && (
        <div className="dye-actions">
          <Popconfirm title="این پارت حذف شود؟" onConfirm={del}><Button danger icon={<DeleteOutlined />}>حذف پارت</Button></Popconfirm>
        </div>
      )}
    </>
  );

  return (
    <Modal open={open} onCancel={onClose} footer={null} destroyOnHidden
      className={screens.md ? "dye-modal" : "gx-modal-full dye-modal"} width={screens.md ? 620 : "100vw"}
      title={lot ? <span>پارت <b>{lbl(lot.label)}</b> <span className="dye-mute">· {fa(lot.entry_date_fa)}</span></span> : "پارتِ تازه"}>
      <div className="dye-sheet">
        {isNew && <DraftBar draft={draft} what="پارتِ نیمه‌کاره" onRestore={(v) => v && setF(v)} />}
        {lot ? detail : form}
      </div>
    </Modal>
  );
}
